import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { wsNoteAIEditUrl } from "utils/urls";

function createRequestId() {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    return `note-ai-edit-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function useNoteAIEditWebSocket({ enabled = true, noteNo, spaceUuid }) {
    const wsRef = useRef(null);
    const pendingMessageRef = useRef(null);
    const currentRequestIdRef = useRef(null);
    const [status, setStatus] = useState("idle");
    const [responseText, setResponseText] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [hasResult, setHasResult] = useState(false);
    const [isConnected, setIsConnected] = useState(false);

    const connectionReady = useMemo(() => enabled && !!spaceUuid && !!noteNo, [enabled, noteNo, spaceUuid]);

    const handleSocketMessage = useCallback((event) => {
        const data = JSON.parse(event.data || "{}");
        const type = data?.type;
        const payload = data?.payload || {};
        const requestId = payload?.request_id;

        if (requestId && requestId !== currentRequestIdRef.current) {
            return;
        }

        if (type === "ai_edit.started") {
            setStatus("streaming");
            setResponseText("");
            setErrorMessage("");
            setHasResult(false);
            return;
        }

        if (type === "ai_edit.delta") {
            setStatus("streaming");
            setResponseText((prev) => prev + (payload.content || ""));
            setHasResult(true);
            return;
        }

        if (type === "ai_edit.completed") {
            setStatus("completed");
            setResponseText((prev) => (payload.content != null ? payload.content : prev));
            setHasResult(true);
            return;
        }

        if (type === "ai_edit.stopped") {
            setStatus("stopped");
            setResponseText((prev) => (payload.content != null ? payload.content : prev));
            setHasResult(true);
            return;
        }

        if (type === "ai_edit.error") {
            setStatus("error");
            setErrorMessage(payload.message || "AI 편집 요청에 실패했습니다.");
        }
    }, []);

    const connectWebSocket = useCallback(() => {
        if (!connectionReady || wsRef.current) {
            return;
        }

        const socket = new WebSocket(wsNoteAIEditUrl(spaceUuid, noteNo));
        wsRef.current = socket;

        socket.onopen = () => {
            setIsConnected(true);
            if (pendingMessageRef.current) {
                socket.send(JSON.stringify(pendingMessageRef.current));
                pendingMessageRef.current = null;
            }
        };

        socket.onmessage = handleSocketMessage;
        socket.onerror = () => {
            setErrorMessage("AI 편집 소켓 연결에 실패했습니다.");
        };
        socket.onclose = () => {
            setIsConnected(false);
            wsRef.current = null;
        };
    }, [connectionReady, handleSocketMessage, noteNo, spaceUuid]);

    useEffect(() => {
        if (connectionReady) {
            connectWebSocket();
        }

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [connectWebSocket, connectionReady]);

    const sendOrQueue = useCallback(
        (message) => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify(message));
                return;
            }

            pendingMessageRef.current = message;
            connectWebSocket();
        },
        [connectWebSocket],
    );

    const startRequest = useCallback(
        ({ actionType, attemptCount, documentPlainText, prompt, selectedMarkup, selectedText, selectedTokens }) => {
            const requestId = createRequestId();
            currentRequestIdRef.current = requestId;
            setStatus("connecting");
            setResponseText("");
            setErrorMessage("");
            setHasResult(false);

            sendOrQueue({
                action: "start",
                payload: {
                    request_id: requestId,
                    action_type: actionType,
                    prompt,
                    selected_text: selectedText,
                    selected_markup: selectedMarkup,
                    selected_tokens: selectedTokens,
                    document_plain_text: documentPlainText,
                    attempt_count: attemptCount,
                },
            });

            return requestId;
        },
        [sendOrQueue],
    );

    const stopRequest = useCallback(() => {
        if (!currentRequestIdRef.current) {
            return;
        }

        sendOrQueue({
            action: "stop",
            payload: {
                request_id: currentRequestIdRef.current,
            },
        });
    }, [sendOrQueue]);

    const reset = useCallback(() => {
        currentRequestIdRef.current = null;
        setStatus("idle");
        setResponseText("");
        setErrorMessage("");
        setHasResult(false);
    }, []);

    return {
        errorMessage,
        hasResult,
        isConnected,
        isStreaming: status === "streaming" || status === "connecting",
        responseText,
        reset,
        startRequest,
        status,
        stopRequest,
    };
}
